import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Popconfirm, message, Space, Input, Select } from 'antd';
import {
  fetchPendingProjectsAsync,
  acceptProjectAsync,
  rejectProjectAsync,
} from '../../features/Projects/pendingProjectSlice';

const { Search } = Input;
const { Option } = Select;

const PendingProjectsPage = () => {
  const dispatch = useDispatch();
  const { projects, loading, pagination } = useSelector(
    (state) => state.pendingProjects
  );

  // Local state for query parameters (filters)
  const [filters, setFilters] = useState({
    search: '',
    status: ['pending', 'rejected'], // default filter values
    page: 1,
    pageSize: 10,
  });

  // Fetch projects whenever filters change
  useEffect(() => {
    dispatch(fetchPendingProjectsAsync(filters));
  }, [dispatch, filters]);

  // Handle search input change
  const handleSearch = (value) => {
    setFilters((prev) => ({
      ...prev,
      search: value,
      page: 1, // reset to first page on search
    }));
  };

  // Handle status filter change
  const handleStatusChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      status: value,
      page: 1, // reset to first page on filter change
    }));
  };

  // Handle table pagination and page size change
  const handleTableChange = (paginationInfo) => {
    setFilters((prev) => ({
      ...prev,
      page: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  // Accept project action
  const handleAccept = (projectId) => {
    dispatch(acceptProjectAsync(projectId))
      .unwrap()
      .then(() => {
        message.success('Project accepted successfully');
        // Refresh projects with current filters
        dispatch(fetchPendingProjectsAsync(filters));
      })
      .catch(() => message.error('Failed to accept project'));
  };

  // Reject project action
  const handleReject = (projectId) => {
    dispatch(rejectProjectAsync(projectId))
      .unwrap()
      .then(() => {
        message.success('Project rejected successfully');
        // Refresh projects with current filters
        dispatch(fetchPendingProjectsAsync(filters));
      })
      .catch(() => message.error('Failed to reject project'));
  };

  // Table columns definition
  const columns = [
    {
      title: 'Unique ID',
      dataIndex: 'unique_id',
      key: 'unique_id',
    },
    {
      title: 'Project Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Client Email',
      dataIndex: ['user', 'email'],
      key: 'user',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Popconfirm
            title="Are you sure you want to accept this project?"
            onConfirm={() => handleAccept(record.id)}
          >
            <Button type="primary">Accept</Button>
          </Popconfirm>
          <Popconfirm
            title="Are you sure you want to reject this project?"
            onConfirm={() => handleReject(record.id)}
          >
            <Button danger>Reject</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <h1>Pending Projects</h1>
      {/* Filters */}
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="Search projects"
          onSearch={handleSearch}
          enterButton
          allowClear
          style={{ width: 300 }}
        />
        <Select
          mode="multiple"
          placeholder="Filter by status"
          defaultValue={['pending', 'rejected']}
          onChange={handleStatusChange}
          style={{ width: 200 }}
        >
          <Option value="pending">Pending</Option>
          <Option value="rejected">Rejected</Option>
        </Select>
      </Space>
      {/* Projects Table */}
      <Table
        dataSource={projects}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          // Use backend pagination values; fallback to local filters if needed
          current: pagination?.current_page || filters.page,
          pageSize: pagination?.per_page || filters.pageSize,
          total: pagination?.total || 0,
          showSizeChanger: false,
        }}
        onChange={handleTableChange}
      />
    </div>
  );
};

export default PendingProjectsPage;
